/**
 * Formation State Types
 *
 * Defines the state-driven formation system where modifiers update a FormationState
 * object that the renderer interprets to position players.
 */

export type PersonnelGroup = 'standard' | 'heavy' | 'army_2t' | 'army_3t' | '21' | '12' | '11' | '10' | '20'
export type LineBalance = 'balanced' | 'litter' | 'rotten'
export type ReceiverWidth = 'standard' | 'wide' | 'tight'
export type YAlignment = 'attached' | 'flex' | 'open' | 'nasty'
export type ZAlignment = 'standard' | 'sexy'
export type SlotCompression = 'standard' | 'pinch'
export type BackfieldScheme = 'i_form' | 'split' | 'oregon' | 'pistol' | 'gun'
export type BackfieldAlign =
	| 'standard'
	| 'ace'
	| 'deuce'
	| 'ray'
	| 'leo'
	| 'jack'
	| 'joker'
export type HBackLocation = 'none' | 'near' | 'away' | 'u'
export type Strength = 'left' | 'right'

/**
 * Core FormationState interface
 * This is the single source of truth for formation configuration
 * Modifiers update this state, and the renderer uses it to position players
 */
export interface FormationState {
	/** Schema version for migration compatibility */
	schema_version: number

	/** Personnel group (21, 12, 11, heavy, army, etc.) */
	personnel_group: PersonnelGroup

	/** Offensive line balance (balanced, litter, rotten) */
	line_balance: LineBalance

	/** Receiver width adjustment */
	width: ReceiverWidth

	/** Y receiver alignment (attached, flex, open, nasty) */
	y_alignment: YAlignment

	/** Z receiver alignment (standard, sexy=swapped) */
	z_alignment: ZAlignment

	/** Slot receiver compression */
	slot_compression: SlotCompression

	/** Backfield scheme (I, split, oregon, pistol, gun) */
	backfield_scheme: BackfieldScheme

	/** Specific backfield alignment */
	backfield_align: BackfieldAlign

	/** H-back location */
	h_back_loc: HBackLocation

	/** Formation strength side */
	strength: Strength

	/** Custom flags for playbook-specific rules */
	custom_flags?: Record<string, unknown>
}

/**
 * Default formation state
 * Used as the starting point before modifiers are applied
 */
export const DEFAULT_FORMATION_STATE: FormationState = {
	schema_version: 1,
	personnel_group: 'standard',
	line_balance: 'balanced',
	width: 'standard',
	y_alignment: 'attached',
	z_alignment: 'standard',
	slot_compression: 'standard',
	backfield_scheme: 'i_form',
	backfield_align: 'standard',
	h_back_loc: 'none',
	strength: 'right',
}

// Enum value arrays for validation
const VALID_PERSONNEL_GROUPS: readonly PersonnelGroup[] = ['standard', 'heavy', 'army_2t', 'army_3t']
const VALID_LINE_BALANCES: readonly LineBalance[] = ['balanced', 'litter', 'rotten']
const VALID_RECEIVER_WIDTHS: readonly ReceiverWidth[] = ['standard', 'wide', 'tight']
const VALID_Y_ALIGNMENTS: readonly YAlignment[] = ['attached', 'flex', 'open', 'nasty']
const VALID_Z_ALIGNMENTS: readonly ZAlignment[] = ['standard', 'sexy']
const VALID_SLOT_COMPRESSIONS: readonly SlotCompression[] = ['standard', 'pinch']
const VALID_BACKFIELD_SCHEMES: readonly BackfieldScheme[] = ['i_form', 'split', 'oregon', 'pistol', 'gun']
const VALID_BACKFIELD_ALIGNS: readonly BackfieldAlign[] = [
	'standard',
	'ace',
	'deuce',
	'ray',
	'leo',
	'jack',
	'joker',
]
const VALID_H_BACK_LOCATIONS: readonly HBackLocation[] = ['none', 'near', 'away', 'u']
const VALID_STRENGTHS: readonly Strength[] = ['left', 'right']

/**
 * Type guard to validate FormationState objects with strict enum validation
 * This prevents invalid enum values from being accepted
 */
export function isFormationState(obj: unknown): obj is FormationState {
	if (!obj || typeof obj !== 'object') return false
	const state = obj as Partial<FormationState>

	return (
		typeof state.schema_version === 'number' &&
		state.schema_version > 0 &&
		VALID_PERSONNEL_GROUPS.includes(state.personnel_group as PersonnelGroup) &&
		VALID_LINE_BALANCES.includes(state.line_balance as LineBalance) &&
		VALID_RECEIVER_WIDTHS.includes(state.width as ReceiverWidth) &&
		VALID_Y_ALIGNMENTS.includes(state.y_alignment as YAlignment) &&
		VALID_Z_ALIGNMENTS.includes(state.z_alignment as ZAlignment) &&
		VALID_SLOT_COMPRESSIONS.includes(state.slot_compression as SlotCompression) &&
		VALID_BACKFIELD_SCHEMES.includes(state.backfield_scheme as BackfieldScheme) &&
		VALID_BACKFIELD_ALIGNS.includes(state.backfield_align as BackfieldAlign) &&
		VALID_H_BACK_LOCATIONS.includes(state.h_back_loc as HBackLocation) &&
		VALID_STRENGTHS.includes(state.strength as Strength) &&
		(state.custom_flags === undefined || typeof state.custom_flags === 'object')
	)
}

/**
 * Creates a deep clone of a FormationState to prevent mutations
 */
export function cloneFormationState(state: FormationState): FormationState {
	return {
		...state,
		custom_flags: state.custom_flags ? { ...state.custom_flags } : undefined,
	}
}
